import React, { useRef, useState } from "react";
import { uploadChunked } from "../lib/upload";
import { authStore } from "../lib/api";
import { toast } from "./ui/sonner";
import { fmtDT } from "../lib/date";

export const INTAKE_GROUPS = [
  { id: "previsit", zh: "拜访前至少收集", en: "Pre-visit checklist" },
  { id: "feasibility", zh: "可行性分析", en: "Feasibility" },
  { id: "needs", zh: "客户需求", en: "Customer needs" },
];

const CUR_YEAR = new Date().getFullYear();
const YEAR_OPTS = Array.from({ length: CUR_YEAR - 1900 + 1 }, (_, i) => String(CUR_YEAR - i));

export const ADU_TYPE_OPTS = [
  { k: "detached_adu", zh: "独立新建 ADU", en: "Detached ADU" },
  { k: "garage_conversion", zh: "车库改建", en: "Garage conversion" },
  { k: "attached_adu", zh: "附建 ADU（Attached）", en: "Attached ADU" },
  { k: "new_jadu", zh: "新建 JADU", en: "New JADU" },
  { k: "junior_adu", zh: "内部改建 JADU", en: "JADU (interior conversion)" },
  { k: "two_story_adu", zh: "二层 ADU", en: "Two-story ADU" },
  { k: "new_main_house", zh: "新建主屋", en: "New primary house" },
  { k: "new_multifamily", zh: "新建多单位住宅", en: "New multi-family" },
];
export const aduTypeLabel = (k, zh) => { const o = ADU_TYPE_OPTS.find((x) => x.k === k); return o ? (zh ? o.zh : o.en) : k; };

export const INTAKE_SCHEMA = [
  { group: "previsit", key: "city_dept", label: ["所属城市及管辖部门", "City & jurisdiction"], type: "select" },
  { group: "previsit", key: "lot_size", label: ["Lot Size (sf)", "Lot Size (sf)"], type: "text" },
  { group: "previsit", key: "zoning", label: ["Zoning", "Zoning"], type: "select" },
  { group: "previsit", key: "main_house_area", label: ["主屋建筑面积 (sf)", "Main house area (sf)"], type: "number" },
  { group: "previsit", key: "year_built", label: ["主屋建造年份", "Year built"], type: "select", options: YEAR_OPTS },
  { group: "previsit", key: "existing_rooms", label: ["现有房间配置", "Existing rooms"], type: "rooms",
    keys: [{ k: "existing_beds", zh: "卧室", en: "Bed" }, { k: "existing_baths", zh: "卫生间", en: "Bath" },
           { k: "existing_laundry", zh: "洗衣房", en: "Laundry" }, { k: "existing_office", zh: "办公室", en: "Office" }] },
  { group: "previsit", key: "garage_size", label: ["车库面积 (sf)", "Garage size (sf)"], type: "sizeslider", min: 0, max: 1500, noneable: true },
  { group: "previsit", key: "pool_size", label: ["游泳池面积 (sf)", "Pool size (sf)"], type: "sizeslider", min: 0, max: 2000, noneable: true },
  { group: "previsit", key: "accessory_building", label: ["附属建筑(如阳光房/加州房)", "Accessory building"], type: "multicustom", options: ["阳光房", "加州房", "储藏室"] },
  { group: "previsit", key: "unpermitted_structure", label: ["未报批构筑物(如>120sf储藏室/阳光棚)", "Unpermitted structures"], type: "multicustom", options: [] },
  { group: "previsit", key: "special_zones", label: ["特殊区域 (HOA/历史/山火/洪水/特殊规划)", "Special zones"], type: "multi", noneOpt: true },
  { group: "previsit", key: "has_records", label: ["能否找到原始图纸/测量图/旧 Permit", "Original plans/survey/old permits"], type: "select" },

  { group: "feasibility", key: "projectType", top: true, auxKey: "adu_types", label: ["适合的 ADU 类型（可多选）", "ADU types (multi)"], type: "adutypes" },
  { group: "feasibility", key: "adu_location", label: ["建议建 ADU 的位置", "Suggested ADU location"], type: "multi", options: ["前院", "后院"] },
  { group: "feasibility", key: "lot_regular", label: ["地块形状是否规则", "Lot shape regular"], type: "select" },
  { group: "feasibility", key: "mainhouse_affects", label: ["主屋位置是否影响布局", "Main house affects layout"], type: "select" },
  { group: "feasibility", key: "setback_limits", label: ["是否受前/后/侧院或间距限制", "Setback limits"], type: "select" },
  { group: "feasibility", key: "easement", label: ["是否存在 Easement/Utility/Drainage", "Easements"], type: "select" },
  { group: "feasibility", key: "obstacles", label: ["大树/电线杆/泳池/挡土墙/坡地", "Obstacles"], type: "multi",
    options: ["大树", "电线杆", "泳池", "挡土墙", "坡地"], noneOpt: true },
  { group: "feasibility", key: "fire_access", label: ["主屋是否有消防喷淋", "Main house has fire sprinklers"], type: "select" },
  { group: "feasibility", key: "construction_access", label: ["进入后院宽度是否大于5英尺", "Backyard access width >5 ft"], type: "select", options: ["是", "否"] },
  { group: "feasibility", key: "high_risk", label: ["是否存在无法建造/成本异常高风险", "High risk"], type: "select" },

  { group: "needs", key: "purpose", label: ["用途", "Purpose"], type: "select" },
  { group: "needs", key: "want_rooms", label: ["期望房型（数量可调）", "Desired rooms"], type: "rooms",
    keys: [{ k: "want_beds", zh: "卧室", en: "Bed" }, { k: "want_baths", zh: "卫生间", en: "Bath" },
           { k: "want_laundry", zh: "洗衣房", en: "Laundry" }, { k: "want_office", zh: "办公室", en: "Office" }] },
  { group: "needs", key: "stories", label: ["单层/双层", "Stories"], type: "select" },
  { group: "needs", key: "estAduSize", top: true, label: ["预计可建面积 (sf)", "Est. buildable area"], type: "sizeslider", min: 150, max: 1200, velocity: true },
  { group: "needs", key: "budget", top: true, label: ["预算区间 (万元)", "Budget (10k)"], type: "wan" },
  { group: "needs", key: "funding", label: ["资金来源", "Funding"], type: "select" },
  { group: "needs", key: "desired_start", label: ["期望开工时间", "Desired start"], type: "date" },
  { group: "needs", key: "desiredStart", top: true, label: ["期望完工时间", "Target completion"], type: "date" },
  { group: "needs", key: "has_materials", label: ["手头资料 (survey/户型图/Title)", "Materials on hand"], type: "multi", noneOpt: true },
  { group: "needs", key: "competitor_quote", label: ["是否已询别家/有报价参考", "Competitor quote"], type: "select" },
  { group: "needs", key: "primaryConcern", top: true, profile: true, label: ["客户画像", "Customer profile"], type: "text" },  // profile: 在客户详情独立成「客户画像」区块, 不在现场调研表单内
];
export const INTAKE_FORM_FIELDS = INTAKE_SCHEMA.filter((f) => !f.profile);

export const INTAKE_EDITABLE_KEYS = ["city_dept", "zoning", "has_records", "lot_regular", "mainhouse_affects", "setback_limits", "easement", "fire_access", "high_risk", "purpose", "stories", "funding", "competitor_quote", "special_zones", "has_materials"];

export const fieldFilled = (f, top, intake) => {
  if (f.type === "rooms") return (f.keys || []).some(({ k }) => Number(intake[k] || 0) > 0);
  if (f.type === "adutypes") {
    const a = intake[f.auxKey];
    return (Array.isArray(a) && a.length > 0) || !!top[f.key];
  }
  const v = f.top ? top[f.key] : intake[f.key];
  return Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && v !== "");
};

export function RoomsField({ f, intake, setIntakeKey, canEdit, zh, testId }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5" data-testid={testId}>
      {(f.keys || []).map(({ k, zh: lz, en: le }) => {
        const cv = Number(intake[k] ?? 0);
        return (
          <div key={k} className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-500">{zh ? lz : le}</span>
            <button type="button" disabled={!canEdit || cv <= 0} data-testid={`${testId}-${k}-minus`}
              onClick={() => setIntakeKey(k, Math.max(0, cv - 1))}
              className="h-6 w-6 rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:border-indigo-300">−</button>
            <span className="w-5 text-center text-sm font-medium text-slate-700" data-testid={`${testId}-${k}-value`}>{cv}</span>
            <button type="button" disabled={!canEdit || cv >= 5} data-testid={`${testId}-${k}-plus`}
              onClick={() => setIntakeKey(k, Math.min(5, cv + 1))}
              className="h-6 w-6 rounded border border-slate-200 text-slate-600 disabled:opacity-40 hover:border-indigo-300">+</button>
          </div>
        );
      })}
    </div>
  );
}

export function SizeSlider({ f, value, onChange, canEdit, testId }) {
  const lastRef = useRef(null);
  const { min, max, velocity, noneable } = f;
  const isNone = value === "无";
  const num = isNone || value === "" || value == null ? "" : Number(value);
  const handleSlide = (e) => {
    let v = Number(e.target.value);
    if (velocity) {
      const now = performance.now();
      const last = lastRef.current;
      let g = 10;
      if (last) {
        const speed = Math.abs(v - last.v) / Math.max(now - last.t, 1);
        g = speed > 1.2 ? 100 : speed > 0.4 ? 50 : 10;
      }
      lastRef.current = { t: now, v };
      v = Math.min(max, Math.max(min, Math.round(v / g) * g));
    }
    onChange(v);
  };
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      {noneable && (
        <button type="button" disabled={!canEdit} onClick={() => onChange(isNone ? "" : "无")} data-testid={`${testId}-none`}
          className={`text-xs rounded-full px-2.5 py-1 border shrink-0 transition-colors ${isNone ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}>无</button>
      )}
      <input type="range" min={min} max={max} step={velocity ? 1 : 10} value={num === "" ? min : num}
        disabled={!canEdit || isNone} onChange={handleSlide} data-testid={`${testId}-slider`} className={`flex-1 min-w-0 accent-indigo-600 ${isNone ? "opacity-30" : ""}`} />
      <div className="relative w-24 shrink-0">
        <input type="number" min={min} max={max} disabled={!canEdit || isNone} value={num}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          data-testid={`${testId}-input`}
          className="w-full text-sm rounded-lg border border-slate-200 bg-white px-2 py-1 pr-7 text-slate-700 focus:border-indigo-400 focus:outline-none" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">sf</span>
      </div>
    </div>
  );
}

export function MultiChips({ opts, value, onChange, canEdit, noneOpt, testId }) {
  const cur = Array.isArray(value) ? value : [];
  const all = noneOpt ? ["无", ...opts.filter((o) => o !== "无")] : opts;
  const toggle = (o) => {
    if (noneOpt && o === "无") { onChange(cur.includes("无") ? [] : ["无"]); return; }
    onChange(cur.includes(o) ? cur.filter((x) => x !== o) : [...cur.filter((x) => x !== "无"), o]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {all.map((o) => (
        <button key={o} type="button" disabled={!canEdit} onClick={() => toggle(o)} data-testid={`${testId}-opt-${o}`}
          className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${cur.includes(o) ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}>{o}</button>
      ))}
    </div>
  );
}

export function AduTypesField({ top, intake, setIntakeKey, setTopKey, canEdit, zh, testId }) {
  const [txt, setTxt] = useState("");
  const sel = Array.isArray(intake.adu_types) ? intake.adu_types : (top.projectType ? [top.projectType] : []);
  const apply = (next) => { setIntakeKey("adu_types", next); setTopKey("projectType", next[0] || ""); };
  const toggle = (k) => apply(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]);
  const addCustom = () => {
    const v = txt.trim().slice(0, 40);
    if (!v) return;
    const hit = ADU_TYPE_OPTS.find((o) => o.zh === v || o.en.toLowerCase() === v.toLowerCase());
    const k = hit ? hit.k : v;
    if (!sel.includes(k)) apply([...sel, k]);
    setTxt("");
  };
  const customs = sel.filter((k) => !ADU_TYPE_OPTS.some((o) => o.k === k));
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
      {ADU_TYPE_OPTS.map((o) => (
        <button key={o.k} type="button" disabled={!canEdit} onClick={() => toggle(o.k)} data-testid={`${testId}-opt-${o.k}`}
          className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${sel.includes(o.k) ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}>{zh ? o.zh : o.en}</button>
      ))}
      {customs.map((k) => (
        <span key={k} data-testid={`${testId}-custom-${k}`} className="inline-flex items-center gap-1 text-xs rounded-full pl-2.5 pr-1.5 py-1 border bg-violet-600 border-violet-600 text-white" title={zh ? "自定义项目类型" : "Custom project type"}>
          {k}{canEdit && <button type="button" onClick={() => toggle(k)} data-testid={`${testId}-custom-remove-${k}`} className="rounded-full hover:bg-white/20 px-1 leading-none" aria-label={zh ? "移除" : "Remove"}>×</button>}
        </span>
      ))}
      {canEdit && (
        <span className="inline-flex items-center gap-1">
          <input data-testid={`${testId}-custom-input`} value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} placeholder={zh ? "+ 自定义类型，如 新建主屋" : "+ Custom type, e.g. New primary house"} className="text-xs rounded-full border border-dashed border-indigo-300 px-2.5 py-1 w-48 focus:outline-none focus:border-indigo-500" />
          <button type="button" disabled={!txt.trim()} onClick={addCustom} data-testid={`${testId}-custom-add`} className="text-xs rounded-full px-2.5 py-1 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40">{zh ? "添加" : "Add"}</button>
        </span>
      )}
    </div>
  );
}

const chip = (on) => `text-xs rounded-full px-2.5 py-1 border transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`;

export function MultiCustom({ opts, value, onChange, canEdit, zh, testId }) {
  const [txt, setTxt] = useState("");
  const cur = Array.isArray(value) ? value : (value ? [String(value)] : []);
  const toggle = (o) => {
    if (o === "无") { onChange(cur.includes("无") ? [] : ["无"]); return; }
    onChange(cur.includes(o) ? cur.filter((x) => x !== o) : [...cur.filter((x) => x !== "无"), o]);
  };
  const addCustom = () => {
    const t = txt.trim(); if (!t) return;
    if (!cur.includes(t)) onChange([...cur.filter((x) => x !== "无"), t]);
    setTxt("");
  };
  const customVals = cur.filter((x) => x !== "无" && !opts.includes(x));
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 items-center">
        {["无", ...opts].map((o) => (
          <button key={o} type="button" disabled={!canEdit} onClick={() => toggle(o)} data-testid={`${testId}-opt-${o}`} className={chip(cur.includes(o))}>{o}</button>
        ))}
        {customVals.map((o) => (
          <button key={o} type="button" disabled={!canEdit} onClick={() => toggle(o)} data-testid={`${testId}-custom-${o}`} className={chip(true)} title={zh ? "点击移除" : "Click to remove"}>{o} ×</button>
        ))}
        {canEdit && (
          <span className="inline-flex items-center gap-1">
            <input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()}
              placeholder={zh ? "自定义…" : "Custom…"} data-testid={`${testId}-custom-input`}
              className="text-xs rounded-lg border border-slate-200 px-2 py-1 w-28 focus:border-indigo-400 focus:outline-none" />
            <button type="button" onClick={addCustom} data-testid={`${testId}-custom-add`} className="text-xs rounded px-2 py-1 border border-indigo-200 text-indigo-600 hover:bg-indigo-50">＋</button>
          </span>
        )}
      </div>
    </div>
  );
}

// 竞品询价详情 — 仅销售端渲染, 设计部镜像不展示
export function CompetitorQuoteDetail({ api, leadId, intake, setIntakeKey, canEdit, zh, testId, initialInsight }) {
  const [busy, setBusy] = useState(false);
  const [insight, setInsight] = useState(initialInsight || null);
  const [aiBusy, setAiBusy] = useState(false);
  const planRef = useRef(null); const quoteRef = useRef(null);
  const runInsight = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/competitor-insight`);
      setInsight(data);
      toast.success(zh ? "AI 竞品分析已生成" : "Insight generated");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "分析失败" : "Failed")); }
    setAiBusy(false);
  };
  const dl = (fid) => `${api.defaults.baseURL}/files/${fid}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;
  const uploadTo = async (fileList, key) => {
    setBusy(true);
    try {
      const items = Array.isArray(intake[key]) ? [...intake[key]] : [];
      for (const f of Array.from(fileList || [])) {
        const fin = await uploadChunked(api, f);
        items.push({ fileId: fin.fileId, filename: f.name });
      }
      setIntakeKey(key, items);
      toast.success(zh ? "已上传，保存概览后生效" : "Uploaded — save to apply");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "上传失败" : "Upload failed")); }
    setBusy(false);
  };
  const FileChips = ({ k }) => (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {(Array.isArray(intake[k]) ? intake[k] : []).map((f, i) => (
        <span key={i} className="inline-flex items-center gap-1 text-[11px] rounded border border-slate-200 bg-white px-1.5 py-0.5" data-testid={`${testId}-${k}-file-${i}`}>
          <a href={dl(f.fileId)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline max-w-[140px] truncate">{f.filename}</a>
          {canEdit && <button type="button" onClick={() => setIntakeKey(k, intake[k].filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500">×</button>}
        </span>
      ))}
    </div>
  );
  const YesNo = ({ k, tid }) => (
    <div className="flex gap-1.5">
      {["有", "无"].map((o) => (
        <button key={o} type="button" disabled={!canEdit} onClick={() => setIntakeKey(k, o)} data-testid={`${tid}-${o}`} className={chip(intake[k] === o)}>{o}</button>
      ))}
    </div>
  );
  return (
    <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/40 p-3 space-y-2.5" data-testid={testId}>
      <p className="text-[11px] font-semibold text-violet-700">{zh ? "竞品询价详情" : "Competitor quote details"}</p>
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-2.5">
        <div className="space-y-1">
          <label className="text-[11px] text-slate-500">{zh ? "是否有图纸" : "Has drawings"}</label>
          <YesNo k="competitor_has_plans" tid={`${testId}-has-plans`} />
          {intake.competitor_has_plans === "有" && (
            <div>
              {canEdit && (
                <>
                  <input ref={planRef} type="file" multiple className="hidden" accept="image/*,application/pdf,.dwg" onChange={(e) => { uploadTo(Array.from(e.target.files), "competitor_plan_files"); e.target.value = ""; }} data-testid={`${testId}-plan-input`} />
                  <button type="button" disabled={busy} onClick={() => planRef.current?.click()} data-testid={`${testId}-plan-upload`} className="text-[11px] text-white bg-violet-600 hover:bg-violet-700 rounded px-2 py-1 disabled:opacity-50">{zh ? "上传图纸" : "Upload drawings"}</button>
                </>
              )}
              <FileChips k="competitor_plan_files" />
            </div>
          )}
        </div>
        <div className="space-y-1">
          <label className="text-[11px] text-slate-500">{zh ? "是否有报价表或总价" : "Has quote sheet / total price"}</label>
          <YesNo k="competitor_has_quote" tid={`${testId}-has-quote`} />
          {intake.competitor_has_quote === "有" && (
            <div className="space-y-1">
              <div className="relative w-40">
                <input type="number" disabled={!canEdit} value={intake.competitor_quote_amount ?? ""} onChange={(e) => setIntakeKey("competitor_quote_amount", e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder={zh ? "对方总价" : "Total price"} data-testid={`${testId}-amount`}
                  className="w-full text-sm rounded-lg border border-slate-200 bg-white px-2 py-1 pl-5 focus:border-indigo-400 focus:outline-none" />
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
              </div>
              {canEdit && (
                <>
                  <input ref={quoteRef} type="file" multiple className="hidden" accept="image/*,application/pdf,.xls,.xlsx" onChange={(e) => { uploadTo(Array.from(e.target.files), "competitor_quote_files"); e.target.value = ""; }} data-testid={`${testId}-quote-input`} />
                  <button type="button" disabled={busy} onClick={() => quoteRef.current?.click()} data-testid={`${testId}-quote-upload`} className="text-[11px] text-white bg-violet-600 hover:bg-violet-700 rounded px-2 py-1 disabled:opacity-50">{zh ? "上传报价表" : "Upload quote"}</button>
                </>
              )}
              <FileChips k="competitor_quote_files" />
            </div>
          )}
        </div>
        <div className="md:col-span-2 space-y-1">
          <label className="text-[11px] text-slate-500">{zh ? "竞品公司名称" : "Competitor company"}</label>
          <input disabled={!canEdit} value={intake.competitor_company ?? ""} onChange={(e) => setIntakeKey("competitor_company", e.target.value)}
            data-testid={`${testId}-company`} className="w-full max-w-sm text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 focus:border-indigo-400 focus:outline-none" />
        </div>
      </div>
      <div className="pt-2 border-t border-violet-100 space-y-2">
        <button type="button" disabled={aiBusy} onClick={runInsight} data-testid={`${testId}-insight-btn`}
          className="text-[11px] text-white bg-indigo-600 hover:bg-indigo-700 rounded px-2.5 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
          {aiBusy ? (zh ? "分析中…" : "Analyzing…") : (zh ? "✦ AI 竞品分析（对比话术 + 我方优势）" : "✦ AI competitor insight")}
        </button>
        {insight && (
          <div className="rounded-lg bg-white border border-indigo-100 p-2.5 space-y-1.5 text-[12px]" data-testid={`${testId}-insight`}>
            {(insight.advantages || []).length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-indigo-700 mb-0.5">{zh ? "我方优势" : "Our advantages"}</p>
                <ul className="list-disc pl-4 text-slate-600 space-y-0.5">
                  {insight.advantages.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            )}
            {insight.talkTrack && (
              <div>
                <p className="text-[11px] font-semibold text-indigo-700 mb-0.5">{zh ? "对比话术" : "Talk track"}</p>
                <p className="text-slate-600 whitespace-pre-wrap">{insight.talkTrack}</p>
              </div>
            )}
            {insight.priceAnalysis && (
              <div>
                <p className="text-[11px] font-semibold text-indigo-700 mb-0.5">{zh ? "价格分析" : "Price analysis"}</p>
                <p className="text-slate-600">{insight.priceAnalysis}</p>
              </div>
            )}
            {insight.at && <p className="text-[10px] text-slate-300">{fmtDT(insight.at)}{insight.by ? ` · ${insight.by}` : ""}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
